import React, { useMemo } from "react";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import { Editor, defaultEditorModules } from "../../component/Editor";
import { ReferenceEntityName } from "../../api/types/field-type";
import { ReferenceField } from "./ReferenceField";
import { ReferenceFieldValue } from "./ReferenceField/types";
import { useTagList } from "../../api/queries/tag/list";
import { useJourneyTypes } from "../../api/queries/enum/useJourneyTypes";
import { useCampusActivities } from "../../api/queries/enum/useCampusActivities";
import { useFields } from "../../api/queries/enum/useFields";
import { useCountries } from "../../api/queries/enum/useCountries";
import ImageFieldRenderer from "../../component/people/ImageFieldRenderer";
import VideoFieldRenderer from "../../component/people/VideoFieldRenderer";
import { ProfileField } from "../../api/types/crm/profile-fields";

interface DynamicFieldProps {
  fieldId: string;
  fieldType: string;
  label: string;
  value: any;
  onChange: (value: any) => void;
  isRequired?: boolean;
  isSaving?: boolean;
  isDisabled?: boolean;
  error?: string;
  isUnsaved?: boolean;
  placeholder?: string;
  options?: Array<{ label: string; value: any }>;
  className?: string;
  referenceEntity?: ReferenceEntityName | null;
  allowMultiple?: boolean;
  validationRules?: Record<string, any> | null;
  fieldValueId?: string;
  backendFieldId?: string;
}

export const DynamicField: React.FC<DynamicFieldProps> = ({
  fieldId,
  backendFieldId,
  fieldType,
  label,
  value,
  onChange,
  isRequired = false,
  isSaving = false,
  isDisabled = false,
  error,
  isUnsaved = false,
  placeholder,
  options = [],
  className = "",
  referenceEntity,
  allowMultiple = false,
  validationRules,
  fieldValueId,
}) => {
  const apiFieldId = backendFieldId || fieldId;

  const getNumericValue = (inputValue: any): number | null => {
    if (inputValue === null || inputValue === undefined || inputValue === "") {
      return null;
    }

    const numericValue =
      typeof inputValue === "number" ? inputValue : Number(inputValue);

    return Number.isFinite(numericValue) ? numericValue : null;
  };

  // Load reference entity data for multi-select
  const { data: tagsData } = useTagList();
  const { data: journeyTypesData } = useJourneyTypes();
  const { data: campusActivitiesData } = useCampusActivities();
  const { data: fieldsData } = useFields();
  const { data: countriesData } = useCountries();

  const listOptions = useMemo(() => {
    const currentOptions = [...options];
    const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
    const existingValues = new Set(
      currentOptions.map((option) => String(option.value)),
    );

    selectedValues.forEach((selectedValue) => {
      if (
        selectedValue === null ||
        selectedValue === undefined ||
        typeof selectedValue === "object"
      ) {
        return;
      }

      const valueKey = String(selectedValue);
      if (!existingValues.has(valueKey)) {
        currentOptions.push({
          label: valueKey,
          value: selectedValue,
        });
        existingValues.add(valueKey);
      }
    });

    return currentOptions;
  }, [options, value]);

  const referenceOptions = useMemo(() => {
    if (
      !referenceEntity ||
      !allowMultiple ||
      fieldType.toLowerCase() !== "reference"
    ) {
      return [];
    }

    let items: Array<{ id: string; name: string }> = [];

    if (referenceEntity === ReferenceEntityName.Tag) {
      items = tagsData?.data?.results || [];
    } else if (referenceEntity === ReferenceEntityName.JourneyType) {
      items = journeyTypesData?.data?.results || [];
    } else if (referenceEntity === ReferenceEntityName.CampusActivity) {
      items = campusActivitiesData?.data?.results || [];
    } else if (referenceEntity === ReferenceEntityName.Field) {
      items = fieldsData?.data?.results || [];
    } else if (referenceEntity === ReferenceEntityName.Country) {
      items = countriesData?.data?.results || [];
    }

    return items.map((item) => ({
      label: item.name,
      value: item.id,
    }));
  }, [
    referenceEntity,
    allowMultiple,
    fieldType,
    tagsData,
    journeyTypesData,
    campusActivitiesData,
    fieldsData,
    countriesData,
  ]);

  const renderFieldInput = () => {
    const baseProps = {
      id: fieldId,
      value: value || "",
      onChange: (e: any) => onChange(e.target?.value ?? e.value ?? e),
      disabled: isDisabled || isSaving,
      placeholder,
      className: `w-full ${error ? "p-invalid" : ""}`,
    };

    switch (fieldType.toLowerCase()) {
      case "group":
        // Group type is for sections/headers, not actual input fields
        return null;

      case "string":
        return <InputText {...baseProps} value={value || ""} />;

      case "text":
        return (
          <Editor
            content={value || ""}
            onChange={onChange}
            modules={defaultEditorModules}
            placeholder={placeholder}
            className={error ? "p-invalid" : ""}
          />
        );

      case "integer":
        return (
          <InputNumber
            inputId={fieldId}
            value={getNumericValue(value)}
            onValueChange={(e) => onChange(e.value)}
            disabled={isDisabled || isSaving}
            placeholder={placeholder}
            className={`w-full ${error ? "p-invalid" : ""}`}
            useGrouping={false}
          />
        );

      case "double":
        return (
          <InputNumber
            inputId={fieldId}
            value={getNumericValue(value)}
            onValueChange={(e) => onChange(e.value)}
            disabled={isDisabled || isSaving}
            placeholder={placeholder}
            className={`w-full ${error ? "p-invalid" : ""}`}
            minFractionDigits={2}
            maxFractionDigits={2}
          />
        );

      case "date":
        // Parse date string as local date to avoid timezone issues
        // Backend sends "2025-08-13", append "T00:00:00" to parse as local time instead of UTC
        const dateValue = value ? new Date(value + "T00:00:00") : null;

        return (
          <Calendar
            {...baseProps}
            value={dateValue}
            onChange={(e) =>
              onChange(e.value ? e.value.toISOString().split("T")[0] : null)
            }
            dateFormat="yy-mm-dd"
            showIcon
            showOnFocus
          />
        );

      case "boolean":
        return (
          <Checkbox
            inputId={fieldId}
            checked={value || false}
            onChange={(e) => onChange(e.checked)}
            disabled={isDisabled || isSaving}
            className={error ? "p-invalid" : ""}
          />
        );

      case "list":
        if (allowMultiple) {
          const multiValue = Array.isArray(value)
            ? value
            : value
              ? [value]
              : [];
          return (
            <MultiSelect
              {...baseProps}
              value={multiValue}
              options={listOptions}
              onChange={(e) => onChange(e.value)}
              optionLabel="label"
              optionValue="value"
              filter
              emptyMessage="No options available"
              display="chip"
            />
          );
        }
        return (
          <Dropdown
            {...baseProps}
            value={value}
            options={listOptions}
            onChange={(e) => onChange(e.value)}
            optionLabel="label"
            optionValue="value"
            showClear
            filter
            emptyMessage="No options available"
          />
        );

      case "reference":
        if (!referenceEntity) {
          return (
            <div className="text-red-500">Reference entity not specified</div>
          );
        }

        if (allowMultiple) {
          // Backend returns [{id, label}, ...], extract IDs for MultiSelect value
          const multiValue = Array.isArray(value)
            ? value
                .map((item) =>
                  typeof item === "object" && item?.id ? item.id : item,
                )
                .filter(Boolean)
            : [];

          return (
            <MultiSelect
              {...baseProps}
              value={multiValue}
              options={referenceOptions}
              onChange={(e) => onChange(e.value)}
              optionLabel="label"
              optionValue="value"
              filter
              emptyMessage="No options available"
              display="chip"
            />
          );
        }

        // Backend returns {id, label} for single reference fields
        const referenceValue =
          value && typeof value === "object" && value.id
            ? (value as ReferenceFieldValue)
            : null;

        return (
          <ReferenceField
            value={referenceValue}
            onChange={(newValue: ReferenceFieldValue | null) => {
              // Send just the ID to backend, matching other reference field types
              onChange(newValue?.id || null);
            }}
            referenceEntity={referenceEntity}
            disabled={isDisabled || isSaving}
            error={Boolean(error)}
            required={isRequired}
            className={error ? "p-invalid" : ""}
          />
        );

      case "image":
        // Construct a mock ProfileField object for ImageFieldRenderer
        const imageFieldProfile: ProfileField = {
          id: apiFieldId,
          code: apiFieldId, // Fallback
          label: label,
          data_type: "image",
          required: isRequired,
          active: true,
          validation: validationRules || {},
          default_value: null,
        };

        return (
          <ImageFieldRenderer
            fieldType={imageFieldProfile}
            value={value}
            onChange={onChange}
            readOnly={isDisabled || isSaving}
          />
        );

      case "video":
        // Construct a mock ProfileField object for VideoFieldRenderer
        const videoFieldProfile: ProfileField = {
          id: apiFieldId,
          code: apiFieldId, // Fallback
          label: label,
          data_type: "video",
          required: isRequired,
          active: true,
          validation: validationRules || {},
          default_value: null,
        };

        return (
          <VideoFieldRenderer
            fieldType={videoFieldProfile}
            fieldValueId={fieldValueId}
            value={value}
            onChange={onChange}
            readOnly={isDisabled || isSaving}
          />
        );

      default:
        return <InputText {...baseProps} value={value || ""} />;
    }
  };

  return (
    <div className={`field ${className}`}>
      <div className="flex align-items-center justify-content-between mb-2">
        <label
          htmlFor={fieldId}
          className={`font-medium ${isRequired ? "required-field" : ""}`}
        >
          {label}
          {isRequired && <span className="text-red-500 ml-1">*</span>}
          {isUnsaved && <span className="text-blue-500 ml-1">•</span>}
        </label>

        <div className="flex align-items-center gap-2">
          {isSaving && (
            <div className="flex align-items-center gap-1">
              <ProgressSpinner
                style={{ width: "16px", height: "16px" }}
                strokeWidth="4"
              />
              <span className="text-sm text-600">Saving...</span>
            </div>
          )}
        </div>
      </div>

      <div className="relative">
        {renderFieldInput()}

        {isSaving && (
          <div className="absolute top-0 left-0 w-full h-full flex align-items-center justify-content-center bg-white bg-opacity-50 border-round">
            <ProgressSpinner
              style={{ width: "20px", height: "20px" }}
              strokeWidth="4"
            />
          </div>
        )}
      </div>

      {error && (
        <div className="mt-1">
          <Message severity="error" text={error} className="p-2 w-full" />
        </div>
      )}

      <div className="flex justify-content-between align-items-center mt-1">
        <div className="flex align-items-center gap-2">
          {isUnsaved && !isSaving && (
            <small className="text-blue-600">
              <i className="pi pi-circle-fill text-xs"></i> Unsaved changes
            </small>
          )}

          {!isUnsaved && !error && value && (
            <small className="text-green-600">
              <i className="pi pi-check-circle text-xs"></i> Saved
            </small>
          )}
        </div>
      </div>
    </div>
  );
};

export default DynamicField;
